import { Helmet } from 'react-helmet-async';

interface StructuredDataProps {
  type?: 'organization' | 'webpage' | 'article' | 'breadcrumb' | 'faq' | 'event' | 'service';
  data?: Record<string, any>;
}

export default function StructuredData({ type = 'organization', data = {} }: StructuredDataProps) {
  const getStructuredData = () => {
    switch (type) {
      case 'organization':
        return {
          "@context": "https://schema.org",
          "@type": "NGO",
          "name": "Open Hope Initiative",
          "alternateName": "Open Hope",
          "url": "https://openhopeinitiative.com",
          "logo": "https://res.cloudinary.com/ddf1wv47y/image/upload/v1742485745/Open_Hope_gwfcli.png",
          "description": "Building Nigeria's National Emergency Grid Technology. Securing Nigerians and strengthening communities by protecting livelihoods and saving lives.",
          "foundingDate": "2023",
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "NG",
            "addressRegion": "Nigeria"
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "General Inquiries",
            "url": "https://openhopeinitiative.com/contact"
          },
          "sameAs": [
            "https://twitter.com/openhopeNG",
            "https://facebook.com/openhopeinitiative",
            "https://linkedin.com/company/open-hope-initiative"
          ],
          "areaServed": {
            "@type": "Country",
            "name": "Nigeria"
          },
          "knowsAbout": [
            "Emergency Response",
            "Agricultural Security",
            "Community Safety",
            "Disaster Management",
            "Rural Development"
          ],
          "slogan": "Securing Nigerians and strengthening communities",
          ...data
        };
      
      case 'webpage':
        return {
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": data.title || "Open Hope Initiative",
          "description": data.description || "Building Nigeria's National Emergency Grid Technology",
          "url": data.url || "https://openhopeinitiative.com",
          "inLanguage": "en-NG",
          "isPartOf": {
            "@type": "WebSite",
            "name": "Open Hope Initiative",
            "url": "https://openhopeinitiative.com"
          },
          ...data
        };
      
      case 'article':
        return {
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": data.title,
          "description": data.description,
          "image": data.image,
          "datePublished": data.datePublished,
          "dateModified": data.dateModified || data.datePublished,
          "author": {
            "@type": "Organization",
            "name": "Open Hope Initiative"
          },
          "publisher": {
            "@type": "Organization",
            "name": "Open Hope Initiative",
            "logo": {
              "@type": "ImageObject",
              "url": "https://res.cloudinary.com/ddf1wv47y/image/upload/v1742485745/Open_Hope_gwfcli.png"
            }
          },
          ...data
        };
      
      case 'breadcrumb':
        return {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": data.items?.map((item: any, index: number) => ({
            "@type": "ListItem",
            "position": index + 1,
            "name": item.name,
            "item": item.url
          })) || []
        };
      
      case 'faq':
        return {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": data.questions?.map((q: any) => ({
            "@type": "Question",
            "name": q.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": q.answer
            }
          })) || []
        };
      
      case 'event':
        return {
          "@context": "https://schema.org",
          "@type": "Event",
          "name": data.name,
          "description": data.description,
          "startDate": data.startDate,
          "endDate": data.endDate,
          "location": {
            "@type": "Place",
            "name": data.location?.name || "Nigeria",
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "NG"
            }
          },
          "organizer": {
            "@type": "Organization",
            "name": "Open Hope Initiative",
            "url": "https://openhopeinitiative.com"
          },
          ...data
        };
      
      case 'service':
        return {
          "@context": "https://schema.org",
          "@type": "Service",
          "name": data.name,
          "description": data.description,
          "provider": {
            "@type": "NGO",
            "name": "Open Hope Initiative",
            "url": "https://openhopeinitiative.com"
          },
          "areaServed": {
            "@type": "Country",
            "name": "Nigeria"
          },
          "serviceType": data.serviceType || "Emergency Response",
          ...data
        };
      
      default:
        return {};
    }
  };

  const structuredData = getStructuredData();

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
}
